import React, { useEffect, useState, useContext } from 'react';
import { Dimmer, Icon, Loader, List } from 'semantic-ui-react';
import moment from 'moment';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import type { SweetAlertOptions } from 'sweetalert2';
import axiosInstance from '../../helpers/axiosInstance';
import { utils } from '../../helpers/utils';
import { DocumentViewModel } from '../../types/ServerDTO';
import AppContext, { token_config } from '../../AppContext';
import icoMail from '../../assets/images/ico-mail.svg';
import icoMailOpen from '../../assets/images/ico-mail-open.svg';
import { ReactSVG } from 'react-svg';

interface CommunicationHistoryProps {
  communicationHistory: any;
  historyIndex: number;
  isShowCommunicationHistory: boolean;
}

const CommunicationHistory: React.FC<CommunicationHistoryProps> = ({ communicationHistory, historyIndex, isShowCommunicationHistory }) => {
  const [currentHistory, setCurrentHistory] = useState<any>(communicationHistory);
  const [isShowFullContent, setIsShowFullContent] = useState<boolean>(false);
  const [isCommunicationHistoryLoading, setIsCommunicationHistoryLoading] = useState<boolean>(false);
  const { updateNotificationCount } = useContext(AppContext);

  useEffect(() => {
    setCurrentHistory(communicationHistory);
  }, [communicationHistory]);

  const downloadFile = async (downloadLink: string, fileName: string) => {
    axiosInstance.get(downloadLink, { responseType: 'arraybuffer' }).then((response) => {
      if (response.status === 200) {
        utils.DownloadBlobFileWithContentDisposition(response, fileName);
      }
    });
  };

  const putCommunicationHistory = (caseRecordEmailHistoryId: number) => {
    axiosInstance.post(`/Case/ReadUnreadCommunicationHistory/${caseRecordEmailHistoryId}`, {}).then(({ data }) => {
      updateNotificationCount(data);
    });
  }

  const fromNow = moment(currentHistory.createdDate).fromNow();
  let date = '';
  if (moment().year() !== moment(currentHistory.createdDate).year()) {
    date = moment(currentHistory.createdDate).format('MMMM D, YYYY') + ' (' + fromNow + ')';
  } else if (moment().diff(moment(currentHistory.createdDate), 'days') > 0) {
    date = moment(currentHistory.createdDate).format('MMMM D') + ' (' + fromNow + ')';
  } else {
    date = fromNow;
  }

  const decode = (str: string) => {
    const tempElement = document.createElement('div');
    tempElement.innerHTML = str;
    return tempElement.textContent;
  }

  const decodedStr = currentHistory.body ? decode(currentHistory.body?.replace('&lt;', '<').replace('&gt;', '>').replace(/<!--[\s\S]*?-->/g, '')) : '';

  const handleDeleteHistory = (caseRecordEmailHistoryId: number) => {
    const options: SweetAlertOptions = {
      title: 'Are you sure you want to delete?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
    };
    Swal.fire(options).then((result) => {
      if (result.isConfirmed) {
        setIsCommunicationHistoryLoading(true);
        axiosInstance.post(`/Case/DeleteCommunicationHistory/${caseRecordEmailHistoryId}`, {}).then(({ status }) => {
          if (status === 200) {
            toast.success('Communication history deleted successfully');
            window.location.reload();
          }
        });
      }
    });
  };

  let email = ''
  if (currentHistory.email) {
    email = `(${currentHistory.email})`;
  }

  let ccEmail = '';
  if (currentHistory.ccEmailIds && currentHistory.ccEmailIds.length > 0) {
    let tmpCcEmails: any = [];
    currentHistory.ccEmailIds.map((ccEmail: any) => {
      tmpCcEmails.push(ccEmail.email);
    });
    ccEmail = tmpCcEmails.join('; ');
  }

  return (
    <Dimmer.Dimmable as="div" dimmed={isCommunicationHistoryLoading} key={historyIndex}>
      <Dimmer active={isCommunicationHistoryLoading} inverted={true}>
        <Loader>Loading</Loader>
      </Dimmer>
      <List.Item onClick={() => {
        if (!isShowFullContent) {
          setIsShowFullContent(!isShowFullContent);
        }
        if (!currentHistory.isRead) {
          setCurrentHistory({ ...currentHistory, isRead: true });
          putCommunicationHistory(currentHistory.caseRecordEmailHistoryId);
        }
      }} className={!isShowFullContent ? 'cursor-pointer' : ''} style={{ padding: '10px', display: isShowCommunicationHistory ? 'block' : 'none', position: 'relative', borderWidth: !currentHistory.isRead ? '5px' : '1px' }}>
        <List.Content>
          <List.Header onClick={() => {
            setIsShowFullContent(!isShowFullContent);
          }} className='cursor-pointer'>
            {currentHistory.isThirdPartyEmail && (
              <span className='history-time' style={{ cursor: 'pointer', float: 'right', marginRight: '0.5rem' }} onClick={() => {
                handleDeleteHistory(currentHistory.caseRecordEmailHistoryId);
              }}><Icon name='trash' /></span>
            )}
            <span style={{ color: currentHistory.nameColorCode, fontWeight: 'bold' }}>{`${currentHistory.name} ${email}`}</span>
            {currentHistory.attachments !== null && <span style={{ float: 'right' }}><Icon name='paperclip' style={{ opacity: '0.5' }} /></span>}
            <span className='history-time' style={{ float: 'right' }}>{date}</span>
            {isShowFullContent && ccEmail && (
              <div style={{ borderBottom: '1px solid #2224261a' }}>
                <div className='mt-1 mb-1'>Cc: {ccEmail}</div>
              </div>
            )}
          </List.Header>
          <List.Description className={isShowFullContent ? '' : 'communication-single-line-desc cursor-pointer'} style={{ marginTop: '5px' }}>
            {!isShowFullContent && (
              <span onClick={() => setIsShowFullContent(!isShowFullContent)}>{decodedStr}</span>
            )}
            {isShowFullContent && (
              <>
                {currentHistory.subject && (
                  <div className='mt-1 mb-1'><strong>Subject:</strong> {currentHistory.subject}</div>
                )}
                <div className='editor-content'>
                  <div dangerouslySetInnerHTML={{ __html: currentHistory.isHtmlOrPlainText ? currentHistory.body.replace(/href/g, `target='_blank' href`).replace('<br>\n</p>', '</p>\n<br>') : currentHistory.body?.split('\n').join('<br/>') }} />
                  {currentHistory.attachments !== null && (
                    <div style={{ borderTop: '1px solid #2224261a', marginTop: '10px', paddingTop: '5px' }}>
                      {currentHistory.attachments.documents.map((document: DocumentViewModel, documentIndex: number) => (
                        <div key={documentIndex} onClick={() => downloadFile(document.downloadsUrl, document.fileName)} style={{ cursor: 'pointer', marginTop: '5px', textDecoration: 'underline', fontSize: '1.5rem' }}>
                          {document.fileName}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
            {((!isShowFullContent && !currentHistory.isRead) || isShowFullContent) && (
              <div style={{ height: '1rem' }}></div>
            )}
          </List.Description>
        </List.Content>
      </List.Item>
      {!isShowFullContent && !currentHistory.isRead && (
        <ReactSVG width='16' height='16' src={icoMail} title='Mark as read' className='envelope-icon' onClick={() => {
          setIsShowFullContent(true);
          setCurrentHistory({ ...currentHistory, isRead: true });
          putCommunicationHistory(currentHistory.caseRecordEmailHistoryId);
        }} />
      )}
      {isShowFullContent && currentHistory.isRead && (
        <ReactSVG width='16' height='16' src={icoMailOpen} title='Mark as unread' className='envelope-icon' onClick={() => {
          setIsShowFullContent(false);
          setCurrentHistory({ ...currentHistory, isRead: false });
          putCommunicationHistory(currentHistory.caseRecordEmailHistoryId);
        }} />
      )}
    </Dimmer.Dimmable>
  );
};

export default CommunicationHistory;